import { useEffect, useState } from 'react';

import {
  fetchActiveRequestForRegister,
  fetchRequestLoad,
  fetchRequestReadings,
} from './kioskApi';
import { mergeRequestLoad, sortReadings } from './kioskTransforms';

const IDLE_STATE = { type: 'idle', message: '' };

export function useCurrentKioskRequest({
  requestRefreshTick,
  selectedRegisterId,
  setRequestLoads,
  visibilityRefreshTick,
}) {
  const [currentRequest, setCurrentRequest] = useState(null);
  const [currentRequestLoad, setCurrentRequestLoad] = useState(null);
  const [readings, setReadings] = useState([]);
  const [requestState, setRequestState] = useState(IDLE_STATE);
  const [readingsState, setReadingsState] = useState(IDLE_STATE);

  useEffect(() => {
    if (!selectedRegisterId) {
      setCurrentRequest(null);
      setReadings([]);
      setReadingsState(IDLE_STATE);
      setRequestState(IDLE_STATE);
      return;
    }

    let active = true;

    async function loadRequestForRegister() {
      setRequestState({ type: 'pending', message: 'Loading current request...' });

      try {
        const requestPayload = await fetchActiveRequestForRegister(selectedRegisterId);
        if (!active) {
          return;
        }

        setCurrentRequest(requestPayload);
        setReadingsState(IDLE_STATE);
        setRequestState({
          type: 'success',
          message: requestPayload ? '' : 'ยังไม่มีคำขอการชั่งสำหรับสถานีนี้ ระบบจะสร้างคำขอใหม่เมื่อสถานีพร้อม.',
        });
      } catch (error) {
        if (!active) {
          return;
        }

        setCurrentRequest(null);
        setReadings([]);
        setReadingsState(IDLE_STATE);
        setRequestState({
          type: 'error',
          message: error instanceof Error ? error.message : 'ไม่สามารถโหลดคำขอการชั่งปัจจุบันสำหรับสถานีนี้ได้.',
        });
      }
    }

    loadRequestForRegister();
    return () => {
      active = false;
    };
  }, [requestRefreshTick, selectedRegisterId, visibilityRefreshTick]);

  useEffect(() => {
    if (!currentRequest?.requestId) {
      setReadings([]);
      setReadingsState(IDLE_STATE);
      return;
    }

    const readingIds = [...new Set(
      [currentRequest.requestId, currentRequest.readingsSourceRequestId].filter(Boolean),
    )];
    let active = true;

    async function loadReadings() {
      setReadingsState({ type: 'pending', message: 'กำลังโหลดข้อมูลการชั่ง...' });

      try {
        const arrays = await Promise.all(readingIds.map(fetchRequestReadings));
        const allReadings = arrays.flat();
        const unique = sortReadings(
          allReadings.filter((reading, index, items) => (
            items.findIndex((item) => item.id === reading.id) === index
          )),
        );
        if (!active) {
          return;
        }

        setReadings(unique);
        setReadingsState({ type: 'success', message: '' });
      } catch {
        if (!active) {
          return;
        }

        setReadings([]);
        setReadingsState({
          type: 'error',
          message: 'ไม่สามารถโหลดข้อมูลการชั่งสำหรับคำขอที่เลือกได้.',
        });
      }
    }

    loadReadings();
    return () => {
      active = false;
    };
  }, [currentRequest?.readingsSourceRequestId, currentRequest?.requestId, visibilityRefreshTick]);

  useEffect(() => {
    if (!currentRequest?.requestLoadId) {
      setCurrentRequestLoad(null);
      return;
    }

    let active = true;

    async function loadCurrentRequestLoad() {
      try {
        const payload = await fetchRequestLoad(currentRequest.requestLoadId, selectedRegisterId);
        if (!active) {
          return;
        }

        setCurrentRequestLoad(payload);
        setRequestLoads((currentItems) => mergeRequestLoad(currentItems, payload));
      } catch {
        if (!active) {
          return;
        }

        setCurrentRequestLoad({
          loadId: currentRequest.requestLoadId,
          registerId: selectedRegisterId,
          name: currentRequest.requestLoadId,
          direction: currentRequest.requestDirection,
          linkedOutboundLoadId: null,
        });
      }
    }

    loadCurrentRequestLoad();
    return () => {
      active = false;
    };
  }, [
    currentRequest?.requestDirection,
    currentRequest?.requestLoadId,
    selectedRegisterId,
    setRequestLoads,
    visibilityRefreshTick,
  ]);

  return {
    currentRequest,
    currentRequestLoad,
    readings,
    readingsState,
    requestState,
    setCurrentRequest,
    setCurrentRequestLoad,
    setReadings,
    setReadingsState,
    setRequestState,
  };
}
